import type { ColDef, ColumnState, GridApi, GridReadyEvent } from "ag-grid-community";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAgGridStateByKey, setAgGridState } from "../global/agGridStateSlice";

export function useAgGridReduxState<T = any>(key: string, baseColumns?: ColDef<T>[]) {
  const dispatch = useDispatch();
  const saved = useSelector(selectAgGridStateByKey(key));
  const apiRef = useRef<GridApi | null>(null);

  // LocalStorage partial state key (grid instance key)
  const LS_KEY = `${key}_partialState`;

  type PartialState = {
    columns: Array<{
      colId?: string;
      hide?: boolean;
      width?: number;
      pinned?: "left" | "right" | null;
      sort?: "asc" | "desc" | null;
    }>;
    filterModel?: any;
  };

  // Build merged columns from (priority) redux columnState -> localStorage partial -> baseColumns
  const mergedColumns: ColDef<T>[] | undefined = useMemo(() => {
    if (!baseColumns) return undefined;

    const applyPartial = (
      partial: Array<{
        colId?: string;
        hide?: boolean;
        width?: number;
        pinned?: "left" | "right" | null;
        sort?: "asc" | "desc" | null;
      }>,
    ): ColDef<T>[] => {
      const used = new Set<string>();
      const ordered: ColDef<T>[] = [];
      for (const s of partial) {
        const id = s.colId;
        if (!id) continue;
        const base = baseColumns.find((c) => (c.colId || (c as any).field) === id);
        if (!base) continue;
        used.add(id);
        ordered.push({
          ...base,
          hide: typeof s.hide === "boolean" ? s.hide : base.hide,
          width: typeof s.width === "number" && s.width > 0 ? s.width : base.width,
          pinned: s.pinned === "left" || s.pinned === "right" ? s.pinned : base.pinned,
          sort: s.sort,
        });
      }
      for (const base of baseColumns) {
        const keyId = base.colId || (base as any).field;
        if (!keyId || used.has(keyId)) continue;
        ordered.push(base);
      }
      return ordered;
    };

    // 1. Redux full columnState present -> convert to partial and merge
    if (saved.columnState && Array.isArray(saved.columnState)) {
      const partial = (saved.columnState as ColumnState[]).map((c) => ({
        colId: c.colId,
        hide: c.hide,
        width: c.width,
        pinned: c.pinned === "left" || c.pinned === "right" ? c.pinned : null,
        sort: c.sort,
      }));
      return applyPartial(partial);
    }

    // 2. LocalStorage partial
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed: PartialState = JSON.parse(raw);
        if (Array.isArray(parsed?.columns)) {
          return applyPartial(parsed.columns);
        }
      }
    } catch {
      /* ignore */
    }

    // 3. Fallback baseColumns
    return baseColumns;
  }, [baseColumns, saved.columnState]);

  const persist = useCallback(() => {
    const api = apiRef.current;
    if (!api) {
      return;
    }
    const fullState = api.getColumnState();
    const partial: PartialState = {
      columns: fullState.map((c) => ({
        colId: c.colId,
        hide: c.hide,
        width: c.width,
        pinned: c.pinned === "left" || c.pinned === "right" ? c.pinned : null,
        sort: c.sort as any,
      })),
      filterModel: api.getFilterModel(),
    };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(partial));
    } catch {
      /* ignore storage errors */
    }
    const next = {
      quickFilter: (api.getGridOption("quickFilterText") as string) ?? "",
      columnState: fullState,
      filterModel: partial.filterModel,
    };
    dispatch(setAgGridState({ key, data: next }));
  }, [dispatch, key]);

  // Use lodash debounce for stable, cancellable persistence scheduling
  const onStateUpdated = useMemo(() => debounce(persist, 600), [persist]);

  // Cancel debounced callbacks on unmount to avoid stray updates
  useEffect(() => {
    return () => {
      onStateUpdated.cancel();
    };
  }, [onStateUpdated]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      apiRef.current = params.api;

      // If we had redux columnState we already merged defs; else if only localStorage partial existed we merged; so only autosize if neither.
      const hasReduxCols = saved.columnState && Array.isArray(saved.columnState);
      const hasLSCols = !!localStorage.getItem(LS_KEY);
      if (!hasReduxCols && !hasLSCols) {
        params.api.autoSizeAllColumns();
      }

      if (saved.filterModel != null) {
        params.api.setFilterModel(saved.filterModel);
      } else {
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const parsed: PartialState = JSON.parse(raw);
            if (parsed?.filterModel) {
              params.api.setFilterModel(parsed.filterModel);
            }
          }
        } catch {
          /* ignore */
        }
      }

      if (saved.quickFilter != null) {
        params.api.setGridOption("quickFilterText", saved.quickFilter);
      }
    },
    [saved.columnState, saved.filterModel, saved.quickFilter],
  );

  const setQuickFilterText = useCallback(
    (text: string) => {
      apiRef.current?.setGridOption("quickFilterText", text); // programmatic => ignored by onStateUpdated
      dispatch(setAgGridState({ key, data: { quickFilter: text } }));
    },
    [dispatch, key],
  );

  return { onGridReady, onStateUpdated, setQuickFilterText, mergedColumns };
}
